import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { CustomerOption, PartnerOption, CemeteryOption, ProductOption } from "../types";

export function useOrderData() {
  const { data: customers = [], isLoading: isLoadingCustomers } = useQuery({
    queryKey: ["customers"],
    queryFn: async (): Promise<CustomerOption[]> => {
      const { data, error } = await supabase
        .from("customers")
        .select("id, first_name, last_name, company_name, phone, email")
        .is("deleted_at", null)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data;
    },
  });

  const { data: partners = [], isLoading: isLoadingPartners } = useQuery({
    queryKey: ["partners"],
    queryFn: async (): Promise<PartnerOption[]> => {
      const { data, error } = await supabase
        .from("partners")
        .select("id, name")
        .is("deleted_at", null)
        .order("name");
      if (error) throw error;
      return data;
    },
  });

  const { data: cemeteries = [], isLoading: isLoadingCemeteries } = useQuery({
    queryKey: ["cemeteries"],
    queryFn: async (): Promise<CemeteryOption[]> => {
      const { data, error } = await supabase
        .from("cemeteries")
        .select("id, name, city, requires_approval")
        .is("deleted_at", null)
        .order("name");
      if (error) throw error;
      return data;
    },
  });

  const { data: products = [], isLoading: isLoadingProducts } = useQuery({
    queryKey: ["products"],
    queryFn: async (): Promise<ProductOption[]> => {
      const { data, error } = await supabase
        .from("products")
        .select("id, name, description, unit, price, price_includes_vat, vat_rate, image_url, category")
        .is("deleted_at", null)
        .eq("is_active", true)
        .order("name");
      if (error) throw error;
      return data;
    },
  });

  return {
    customers,
    partners,
    cemeteries,
    products,
    isLoading: isLoadingCustomers || isLoadingPartners || isLoadingCemeteries || isLoadingProducts,
  };
}
